// @flow

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { ReactSelect } from 'styles/Select';
import CountryLocalStore from 'stores/CountryLocalStore';
import CountryLocalActions from 'actions/CountryLocalActions';
import * as Homie from 'styles/Home/StyledHome';
import 'styles/css/Home.css';
import { Auth } from 'utils/ExportCommon';

const StyledValidatedInput = styled.div.attrs({
  style: ({ marginBottom }) => ({
    marginBottom: marginBottom
  })
})`
  position: relative;
`;

const Input = styled.input.attrs({
  style: ({ fontSize }) => ({
    fontSize: fontSize
  })
})`
  display: inline-block;
  height: 35px;
  width: 75%;
  border-radius: 2px;
  background-color: #ffffff;
  padding: 0 0 0 15px;
  border: 1px solid #ececec;
  line-height: 35px;
  font-size: 16px;
  font-weight: bold;
  color: #4a4a4a;
  &::placeholder {
    font-weight: 500;
    color: #9b9b9b;
  }
  &:disabled {
    background: #ececec;
    color: #9b9b9b;
  }
`;

const ErrorMessage = styled.span.attrs({
  className: 'error-message'
})`
  color: #ff1400;
  top: 12px;
  font-size: 14px;
  margin-top: 0px;
  position: relative;
`;

const StyledPhoneInput = styled.div`
  display: block;
`;

const StyledPhoneCode = styled.span`
  display: inline-block;
  width: 25%;
  height: 35px;
  border-color: #ececec;
  background-color: #ececec;
  color: #555555;
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
  line-height: 35px;
  user-select: none;
  text-align: center;
  &.active {
    border-color: #278c7e;
    background-color: #278c7e;
    border: 1px solid #278c7e;
    color: white;
  }
  position: relative;
`;

const Divider = styled.div`
  padding-bottom: 2px;
`;

const StyledIcon = styled.i`
  margin-left: 5px;
  position: relative;
  top: px;
`;

const WhatsappIconWrapper = styled(Homie.WrapperIconWhatsaap)`
  ${props => props.hasTop && 'top: 1px'};
`;

type PropsType = {
  name: string,
  value: string,
  type: string,
  placeholder: string,
  fontSize?: number,
  schema?: any,
  children?: any,
  onValueChange: (value: string, input: string) => void,
  handleCountryCodeClick: () => void,
  handleCountryCodePicked: (element: any) => void,
  pickingCountryCode: boolean,
  countryCode: number,
  maxlength?: number,
  marginBottom?: number,
  hasTop?: boolean,
  hasIcon?: boolean
};

const InputPhoneCode = ({
  name = 'phone',
  value = '',
  type,
  placeholder,
  fontSize = 16,
  schema,
  children,
  onValueChange,
  handleCountryCodeClick,
  handleCountryCodePicked,
  pickingCountryCode,
  countryCode,
  maxlength = 10,
  marginBottom = 15,
  hasTop = true,
  hasIcon = true
}: PropsType) => {
  const [countryCodes, setCountryCodes] = useState([]);
  const [isTouched, setIsTouched] = useState(false);
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setInputValue(value.replace(`+${countryCode}`, ''));
  }, [value]);

  useEffect(() => {
    CountryLocalActions.getPhoneCountryCodes();
    CountryLocalStore.addChangeListener(handleStoreChange);
    return () => {
      CountryLocalStore.removeChangeListener(handleStoreChange);
    };
  }, []);

  const handleStoreChange = () => {
    setCountryCodes(CountryLocalStore.getCodes());
  };

  const handleCountryCode = (): void => {
    handleCountryCodeClick();
  };

  const handleCodePicked = (element: any): void => {
    handleCountryCodePicked(element);
  };

  const handleChange = ({ target }: Event<HTMLInputElement>): void => {
    validationSchema(target.value, false, false);
  };

  const handleBlur = () => {
    validationSchema(inputValue, true, !error);
    if (!isTouched) setIsTouched(true);
  };

  const validationSchema = (
    inputValue: string,
    onBlur: boolean,
    hasError: boolean
  ): void => {
    if (schema) {
      schema
        .validate({ inputValue })
        .then(() => {
          setError('');
        })
        .catch(e => {
          setError(e.message);
        });
    }

    if (inputValue.length <= 10) {
      setInputValue(inputValue);
      if (onBlur && hasError) onValueChange(inputValue, name);
    }
  };

  const defaultCountryCode = CountryLocalStore.getCodeByValue(`${countryCode}`);

  return (
    <StyledPhoneInput>
      <StyledValidatedInput marginBottom={marginBottom}>
        <WhatsappIconWrapper hasTop={hasTop}>
          <StyledPhoneCode
            onClick={handleCountryCode}
            className={pickingCountryCode && 'active'}
          >
            +{countryCode.toString().replace('+', '')}
            {!pickingCountryCode && (
              <StyledIcon className="glyphicon glyphicon-chevron-down icon" />
            )}
          </StyledPhoneCode>
          <Input
            name={name}
            fontSize={fontSize}
            value={inputValue}
            maxlength={maxlength}
            type={type}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {hasIcon && <img className="icon-whatsapp" src={Auth.wsapp} />}
        </WhatsappIconWrapper>
        {children}
        {pickingCountryCode && (
          <div>
            <Divider />
            <Select
              autoFocus
              placeholder="Busca tu país"
              noOptionsMessage={() => 'Sin resultados'}
              defaultValue={defaultCountryCode}
              value={[]}
              styles={ReactSelect}
              onChange={handleCodePicked}
              options={countryCodes}
            />
          </div>
        )}
        {error && isTouched && <ErrorMessage>{error}</ErrorMessage>}
      </StyledValidatedInput>
    </StyledPhoneInput>
  );
};

export default InputPhoneCode;
